<i18n src="./resources/locales.json"></i18n>

<template>
  <div class="card-container">
    <div class="kpis-thresholds-container">
      <div v-for="kpi in categorizedKpis" :key="kpi.category" class="card">
        <div class="card-title">{{ kpi.category }}</div>
        <v-divider></v-divider>
        <div class="card-content custom-scrollbar">
          <EcsBusinessProcessKpiThresholdsInput
            v-for="item in kpi.items"
            :id="item.id"
            :ref="`${kpi.category.toLowerCase()}Thresholds`"
            :key="item.id"
            :warning="getFormattedValue(item.warningThreshold, item.type)"
            :danger="getFormattedValue(item.dangerThreshold, item.type)"
            :type="item.type"
            @change="onKpiThresholdsChange"
          />
        </div>
      </div>
    </div>
    <div class="health-configuration-container">
      <div class="health-configuration-options">
        <v-select
          v-model="selectedBPHealthThresholdsConfigurationId"
          :items="businessProcessHealthThresholdsConfigurations"
          item-value="_id"
          :label="$t('labels.configuractionProfile')"
          item-text="name"
          :height="26"
          hide-details
          attach
          class="health-configuration-input"
          @change="updateSelectedBPHealthThresholdsConfiguration"
        >
        </v-select>
        <v-tooltip v-if="!isDefaultConfigurationSelected" z-index="151" bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon @click="deleteConfiguration()" v-on="on">
              <v-icon size="20">mdi-trash-can-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('tooltips.deleteHealthThresholdsConfiguration') }}</span>
        </v-tooltip>
        <v-tooltip v-if="!isDefaultConfigurationSelected" z-index="151" bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon @click="openDialogToSelectBPs()" v-on="on">
              <v-icon size="20">mdi-cog-transfer-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('tooltips.addOrApplyHealthThresholdsConfigurationToOtherBPs') }}</span>
        </v-tooltip>
      </div>
      <div v-show="hasChanges" class="health-thresholds-options">
        <v-btn depressed text small @click="discardChanges">
          <span v-once>{{ $t('options.discard') }}</span>
        </v-btn>
        <v-btn class="ml-2" depressed :disabled="isPrimaryActionDisabled" small color="primary" @click="onSaveClick">
          {{ saveButtonText }}
        </v-btn>
      </div>
    </div>

    <v-dialog v-model="bpHealthThresholdNameDialog" persistent max-width="290">
      <v-card>
        <v-card-text class="ecs-new-health-config-dialog-text">
          <v-container>
            <v-row>
              <v-col>
                <v-text-field v-model="bpHealthThresholdName" :label="$t('labels.configurationProfileName')" required>
                </v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="bpHealthThresholdNameDialog = false">
            {{ $t('options.cancel') }}
          </v-btn>
          <v-btn color="primary" @click="isDefaultConfigurationSelected ? saveConfiguration() : updateConfiguration()">
            {{ $t('options.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isBPSelectionDialogOpen" max-width="480" scrollable @click:outside="closeDialogToSelectBPs()">
      <v-card>
        <v-card-title>
          {{ $t('dialogs.selectBusinessProcesses.title', { numBPsSelected }) }}
          <v-spacer />
          <v-btn text icon @click="closeDialogToSelectBPs()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <div class="custom-scrollbar">
          <v-list subheader>
            <v-subheader>
              {{ $t('dialogs.selectBusinessProcesses.subTitle') }}
            </v-subheader>
            <v-list-item>
              <v-list-item-action>
                <v-checkbox
                  :value="isBPListChecked()"
                  :input-value="isBPListChecked()"
                  :indeterminate="isBPListIndeterminate()"
                  @click.stop.prevent="selectAllBPs()"
                />
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  {{ $t('dialogs.selectBusinessProcesses.allSupervisedBusinessProcesses') }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item v-for="bp in availablesupervisedBusinessProcesses" :key="bp.businessProcessId">
              <v-list-item-action>
                <v-checkbox
                  :value="isBPChecked(bp)"
                  :input-value="isBPChecked(bp)"
                  @click.stop.prevent="addBPToSelectedList(bp)"
                />
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ bp.businessProcessName }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <div class="primary selection-btn">
            <v-btn depressed class="action-btn primary" :disabled="!selectedBPIds.length" @click="handleApplyChanges">
              {{ $t(applyActionText) }}
            </v-btn>
            <v-divider vertical inset dark class="selection-btn-divider"></v-divider>
            <v-menu max-height="288" content-class="custom-scrollbar">
              <template v-slot:activator="{ on, attrs }">
                <v-btn depressed class="menu-btn primary" :disabled="!selectedBPIds.length" v-bind="attrs" v-on="on">
                  <v-icon right>
                    mdi-menu-down
                  </v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item-group v-model="selectedApplyAction">
                  <v-list-item v-for="(actionName, index) in applyActions" :key="index">
                    <v-list-item-title>{{ $t(actionName) }}</v-list-item-title>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </v-menu>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="isBPHealthThresholdAlreadyAppliedDialog"
      max-width="320"
      @click:outside="isBPHealthThresholdAlreadyAppliedDialog = false"
    >
      <v-card>
        <v-card-text class="pa-4">
          {{ $t('dialogs.selectBusinessProcesses.infoMessage') }}
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn depressed class="action-btn primary" @click="isBPHealthThresholdAlreadyAppliedDialog = false">
            {{ $t('dialogs.selectBusinessProcesses.infoMessageAccept') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <EcsConfirmationDialog
      ref="bpHealthConfirmationDialog"
      :title="confirmDialogTitle"
      :message="confirmDialogText"
      :confirm-button-text="$t('options.confirm')"
      :cancel-button-text="$t('options.cancel')"
      :custom-styles="{ maxWidth: '456px' }"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import EcsBusinessProcessHealthMixins from '@/mixins/ecs-business-process-health/EcsBusinessProcessHealthMixins';
import EcsBusinessProcessKpiThresholdsInput from '@/components/ecs-business-process-monitoring-card/EcsBusinessProcessKpiThresholdsInput';
import EcsConfirmationDialog from '@/components/ecs-confirmation-dialog/EcsConfirmationDialog';

const SNACKBAR_MESSAGE_EVENT = 'snackbar-message';

export default {
  name: 'EcsBusinessProcessMonitoringThresholdsCard',

  components: {
    EcsBusinessProcessKpiThresholdsInput,
    EcsConfirmationDialog
  },

  mixins: [EcsBusinessProcessHealthMixins],

  props: {
    businessProcessId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      updatedThresholds: [],
      selectedBPHealthThresholdsConfigurationId: 0,
      selectedBPHealthThresholdsConfiguration: {},
      bpHealthThresholdNameDialog: false,
      bpHealthThresholdName: '',
      confirmDialogTitle: '',
      confirmDialogText: '',
      isBPSelectionDialogOpen: false,
      isBPHealthThresholdAlreadyAppliedDialog: false,
      selectedBPIds: [],
      applyActions: ['dialogs.selectBusinessProcesses.addAction', 'dialogs.selectBusinessProcesses.addAndApplyAction'],
      selectedApplyAction: 0
    };
  },

  computed: {
    ...mapGetters('BUSINESS_PROCESSES', [
      'supervisedBusinessProcesses',
      'getPreConfiguredBusinessProcessHealthThresholds',
      'getBusinessProcessHealthThresholds',
      'getActiveBusinessProcessHealthThreshold',
      'getDefaultBusinessProcessHealthThreshold'
    ]),

    numBPsSelected() {
      return this.selectedBPIds.length ? `(${this.selectedBPIds.length})` : '';
    },

    applyActionText() {
      return this.applyActions[this.selectedApplyAction];
    },

    businessProcessHealthThresholdsConfigurations() {
      let bpHealthThresholds = this.getPreConfiguredBusinessProcessHealthThresholds(this.businessProcessId);
      bpHealthThresholds.push({ divider: true });

      bpHealthThresholds = bpHealthThresholds.concat(
        this.getBusinessProcessHealthThresholds(this.businessProcessId).filter(item => {
          return item._id !== this.defaultBPHealthThresholdsConfiguration._id;
        })
      );

      return bpHealthThresholds;
    },

    defaultBPHealthThresholdsConfiguration() {
      return this.getDefaultBusinessProcessHealthThreshold(this.businessProcessId);
    },

    availablesupervisedBusinessProcesses() {
      return this.supervisedBusinessProcesses.filter(bp => {
        const bpHealthThresholds = this.getBusinessProcessHealthThresholds(bp.businessProcessId);
        return !bpHealthThresholds.find(bpht => bpht.name === this.selectedBPHealthThresholdsConfiguration.name);
      });
    },

    currentBPKpis() {
      console.log(
        this.selectedBPHealthThresholdsConfiguration
          ? this.selectedBPHealthThresholdsConfiguration.businessProcessKpis
          : {}
      );

      return this.selectedBPHealthThresholdsConfiguration
        ? this.selectedBPHealthThresholdsConfiguration.businessProcessKpis
        : {};
    },

    categorizedKpis() {
      const kpis = [
        {
          category: 'Volume',
          items: this.volumeKpis
        },
        {
          category: 'Reactivity',
          items: this.reactivityKpis
        },
        {
          category: 'Callbacks',
          items: this.callbacksKpis
        }
      ];

      console.log(kpis);

      return kpis;
    },

    volumeKpis() {
      return [
        this.currentBPKpis.businessProcessAnsweredInteractions,
        this.currentBPKpis.businessProcessOverflowInteractions,
        this.currentBPKpis.businessProcessAbandonedInteractions,
        this.currentBPKpis.businessProcessAbandonedPercentage,
        this.currentBPKpis.businessProcessVoiceMails,
        this.currentBPKpis.businessProcessTotalInternalTransferredOut,
        this.currentBPKpis.businessProcessTotalExternalTransferredOut,
        this.currentBPKpis.businessProcessDNCRequestsInShift
      ];
    },

    reactivityKpis() {
      return [
        this.currentBPKpis.businessProcessServiceLevel,
        this.currentBPKpis.businessProcessWaitingInteractions,
        this.currentBPKpis.businessProcessLongestWaitingTime,
        this.currentBPKpis.businessProcessAverageWaitingTime,
        this.currentBPKpis.businessProcessAverageAnswerTime,
        this.currentBPKpis.businessProcessAverageAgentHandlingTime
      ];
    },

    callbacksKpis() {
      return [
        this.currentBPKpis.callbackAverageHandlingTime,
        this.currentBPKpis.callbackRequestedInShift,
        this.currentBPKpis.callbackProcessedInShift,
        this.currentBPKpis.callbackSuccessfulPercentageInShift,
        this.currentBPKpis.callbackFailedPercentageInShift,
        this.currentBPKpis.callbackToBeProcessed,
        this.currentBPKpis.callbackInteractionsPurgedInShift
      ];
    },

    hasChanges() {
      return this.updatedThresholds.length;
    },

    isDefaultConfigurationSelected() {
      const defaultId = this.defaultBPHealthThresholdsConfiguration
        ? this.defaultBPHealthThresholdsConfiguration._id
        : 0;
      return (
        this.selectedBPHealthThresholdsConfigurationId === defaultId ||
        this.selectedBPHealthThresholdsConfiguration.preConfigured
      );
    },

    isPrimaryActionDisabled() {
      return !this.updatedThresholds.every(item => item.isValid);
    },

    saveButtonText() {
      return this.isDefaultConfigurationSelected ? this.$t('options.save') : this.$t('options.update');
    }
  },

  created() {
    this._initData();
    this.$root.$on('init-health-threshold-data', this._initData);
  },

  destroyed() {
    this.$root.$off('init-health-threshold-data', this._initData);
  },

  methods: {
    ...mapActions('BUSINESS_PROCESSES', [
      'addBusinessProcessHealthThresholds',
      'updateBusinessProcessHealthThresholds',
      'deleteBusinessProcessHealthThreshold',
      'addHealthThresholdsConfigurationToOtherBPs',
      'addAndApplyHealthThresholdsConfigurationToOtherBPs'
    ]),

    _initData() {
      const activeBPHealthThresholdsConfiguration = this.getActiveBusinessProcessHealthThreshold(
        this.businessProcessId
      );
      this._setSelectedBPHealthThresholdsConfiguration(activeBPHealthThresholdsConfiguration._id);
    },

    _setSelectedBPHealthThresholdsConfiguration(selectedBPHealthThresholdId) {
      this.selectedBPHealthThresholdsConfigurationId = selectedBPHealthThresholdId;
      this.selectedBPHealthThresholdsConfiguration = this.businessProcessHealthThresholdsConfigurations.find(
        item => item._id === this.selectedBPHealthThresholdsConfigurationId
      );

      this.selectedBPHealthThresholdsConfiguration =
        this.selectedBPHealthThresholdsConfiguration ||
        this.businessProcessHealthThresholdsConfigurations.find(item => !item.divider);

      console.log(
        this.selectedBPHealthThresholdsConfiguration,
        this.businessProcessHealthThresholdsConfigurations,
        this.selectedBPHealthThresholdsConfigurationId
      );
    },

    _resetCurrentActiveBPHealthThresholdsConfiguration() {
      const activeBPHealthThresholdsConfiguration = this.getActiveBusinessProcessHealthThreshold(
        this.businessProcessId
      );
      if (activeBPHealthThresholdsConfiguration) {
        this.updateBusinessProcessHealthThresholds({
          id: activeBPHealthThresholdsConfiguration._id,
          active: false,
          name: activeBPHealthThresholdsConfiguration.name,
          businessProcessId: this.businessProcessId,
          kpis: activeBPHealthThresholdsConfiguration.kpis
        });
      }
    },

    isBPListChecked() {
      return this.selectedBPIds.length === this.availablesupervisedBusinessProcesses.length;
    },

    isBPListIndeterminate() {
      return (
        this.selectedBPIds.length > 0 && this.selectedBPIds.length < this.availablesupervisedBusinessProcesses.length
      );
    },

    isBPChecked(bp) {
      return this.selectedBPIds.includes(bp.businessProcessId);
    },

    addBPToSelectedList(bp) {
      if (!this.isBPChecked(bp)) {
        this.selectedBPIds.push(bp.businessProcessId);
      } else {
        this.selectedBPIds = this.selectedBPIds.filter(bpId => bpId !== bp.businessProcessId);
      }
    },

    selectAllBPs() {
      if (!this.selectedBPIds.length) {
        this.selectedBPIds = [];
        this.availablesupervisedBusinessProcesses.forEach(bp => {
          this.selectedBPIds.push(bp.businessProcessId);
        });
      } else {
        this.selectedBPIds = [];
      }
    },

    onKpiThresholdsChange(data) {
      const kpiIndex = this.updatedThresholds.findIndex(item => item.id === data.id);
      if (kpiIndex === -1) {
        this.updatedThresholds.push(data);
      } else {
        this.updatedThresholds.splice(kpiIndex, 1, data);
      }
    },

    discardChanges() {
      this.updatedThresholds = [];
      this.$refs.volumeThresholds.forEach(item => item.reset());
      this.$refs.reactivityThresholds.forEach(item => item.reset());
      this.$refs.callbacksThresholds.forEach(item => item.reset());
    },

    onSaveClick() {
      this.bpHealthThresholdName = this.isDefaultConfigurationSelected
        ? ''
        : this.selectedBPHealthThresholdsConfiguration.name;
      this.bpHealthThresholdNameDialog = true;
    },

    updateSelectedBPHealthThresholdsConfiguration() {
      this._resetCurrentActiveBPHealthThresholdsConfiguration();
      this._setSelectedBPHealthThresholdsConfiguration(this.selectedBPHealthThresholdsConfigurationId);
      this.updateBusinessProcessHealthThresholds({
        id: this.selectedBPHealthThresholdsConfiguration._id,
        active: true,
        name: this.selectedBPHealthThresholdsConfiguration.name,
        businessProcessId: this.businessProcessId,
        kpis: this.selectedBPHealthThresholdsConfiguration.kpis
      }).then(() => {
        this.discardChanges();
      });
    },

    saveConfiguration() {
      const newKpis = this.defaultBPHealthThresholdsConfiguration.kpis.map(x => x);
      this.updatedThresholds.forEach(item => {
        const { isValid, ...data } = item;
        if (!isValid) {
          return;
        }

        const kpiIndex = newKpis.findIndex(data => data.id === item.id);
        if (kpiIndex === -1) {
          newKpis.push(data);
        } else {
          newKpis.splice(kpiIndex, 1, data);
        }
      });

      this._resetCurrentActiveBPHealthThresholdsConfiguration();
      this.addBusinessProcessHealthThresholds({
        active: true,
        name: this.bpHealthThresholdName,
        businessProcessId: this.businessProcessId,
        kpis: newKpis
      })
        .then(newBPConfig => {
          this.updatedThresholds = [];
          this._setSelectedBPHealthThresholdsConfiguration(newBPConfig._id);
          const successMessage = this.$t('success.addHealthThresholdsConfiguration', {
            name: this.bpHealthThresholdName
          });
          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, { message: successMessage, type: 'success' });
        })
        .catch(() => {
          const errorMessage = this.$t('errors.addHealthThresholdsConfiguration');
          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, { message: errorMessage, type: 'error' });
        })
        .finally(() => {
          this.bpHealthThresholdNameDialog = false;
        });
    },

    updateConfiguration() {
      this.updatedThresholds.forEach(item => {
        const { isValid, ...data } = item;
        if (!isValid) {
          return;
        }

        const kpiIndex = this.selectedBPHealthThresholdsConfiguration.kpis.findIndex(data => data.id === item.id);
        if (kpiIndex === -1) {
          this.selectedBPHealthThresholdsConfiguration.kpis.push(data);
        } else {
          this.selectedBPHealthThresholdsConfiguration.kpis.splice(kpiIndex, 1, data);
        }
      });

      this.updateBusinessProcessHealthThresholds({
        id: this.selectedBPHealthThresholdsConfiguration._id,
        active: true,
        name: this.bpHealthThresholdName,
        businessProcessId: this.businessProcessId,
        kpis: this.selectedBPHealthThresholdsConfiguration.kpis
      })
        .then(() => {
          this.updatedThresholds = [];
          const successMessage = this.$t('success.updateHealthThresholdsConfiguration', {
            name: this.bpHealthThresholdName
          });
          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, { message: successMessage, type: 'success' });
        })
        .catch(() => {
          const errorMessage = this.$t('errors.updateHealthThresholdsConfiguration');
          this.$root.$emit(SNACKBAR_MESSAGE_EVENT, { message: errorMessage, type: 'error' });
        })
        .finally(() => {
          this.bpHealthThresholdNameDialog = false;
        });
    },

    async deleteConfiguration() {
      this.confirmDialogTitle = this.$t('dialogs.deleteHealthThresholdsConfiguration.title');
      this.confirmDialogText = this.$t('dialogs.deleteHealthThresholdsConfiguration.text', {
        name: this.selectedBPHealthThresholdsConfiguration.name
      });
      const acceptDelete = await this.$refs.bpHealthConfirmationDialog.open();

      if (acceptDelete) {
        this.deleteBusinessProcessHealthThreshold({
          id: this.selectedBPHealthThresholdsConfigurationId,
          businessProcessId: this.businessProcessId
        })
          .then(() => {
            this.selectedBPHealthThresholdsConfigurationId = this.defaultBPHealthThresholdsConfiguration._id;
            this.updateSelectedBPHealthThresholdsConfiguration();
            const successMessage = this.$t('success.deleteHealthThresholdsConfiguration', {
              name: this.selectedBPHealthThresholdsConfiguration.name
            });
            this.$root.$emit(SNACKBAR_MESSAGE_EVENT, { message: successMessage, type: 'success' });
          })
          .catch(() => {
            const errorMessage = this.$t('errors.deleteHealthThresholdsConfiguration');
            this.$root.$emit(SNACKBAR_MESSAGE_EVENT, { message: errorMessage, type: 'error' });
          });
      }
    },

    openDialogToSelectBPs() {
      if (!this.availablesupervisedBusinessProcesses.length) {
        this.isBPHealthThresholdAlreadyAppliedDialog = true;
      } else {
        this.isBPSelectionDialogOpen = true;
      }
    },

    closeDialogToSelectBPs() {
      this.isBPSelectionDialogOpen = false;
      this.selectedBPIds = [];
    },

    handleApplyChanges() {
      if (this.selectedApplyAction === 0) {
        this.addConfigurationToOtherBPs();
      } else {
        this.addAndApplyConfigurationToOtherBPs();
      }
    },

    async addConfigurationToOtherBPs() {
      this.confirmDialogTitle = this.$t('dialogs.addHealthThresholdsConfigurationToOtherBPs.title');
      this.confirmDialogText = this.$t('dialogs.addHealthThresholdsConfigurationToOtherBPs.text', {
        name: this.selectedBPHealthThresholdsConfiguration.name
      });
      const acceptAdding = await this.$refs.bpHealthConfirmationDialog.open();
      if (acceptAdding) {
        this.addHealthThresholdsConfigurationToOtherBPs({
          bpHealthThresholds: this.selectedBPHealthThresholdsConfiguration,
          bpIds: this.selectedBPIds
        })
          .then(() => {
            this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
              message: this.$t('success.addHealthThresholdsConfigurationToOtherBPs'),
              type: 'success'
            });
          })
          .catch(() => {
            this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
              message: this.$t('error.addHealthThresholdsConfigurationToOtherBPs'),
              type: 'error'
            });
          })
          .finally(() => {
            this.closeDialogToSelectBPs();
          });
      }
    },

    async addAndApplyConfigurationToOtherBPs() {
      this.confirmDialogTitle = this.$t('dialogs.addAndApplyHealthThresholdsConfigurationToOtherBPs.title');
      this.confirmDialogText = this.$t('dialogs.addAndApplyHealthThresholdsConfigurationToOtherBPs.text', {
        name: this.selectedBPHealthThresholdsConfiguration.name
      });
      const acceptAdding = await this.$refs.bpHealthConfirmationDialog.open();

      if (acceptAdding) {
        this.addAndApplyHealthThresholdsConfigurationToOtherBPs({
          bpHealthThresholds: this.selectedBPHealthThresholdsConfiguration,
          bpIds: this.selectedBPIds
        })
          .then(() => {
            this.$root.$emit('init-health-threshold-data');
            this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
              message: this.$t('success.addAndApplyHealthThresholdsConfigurationToOtherBPs'),
              type: 'success'
            });
          })
          .catch(() => {
            this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
              message: this.$t('error.addAndApplyHealthThresholdsConfigurationToOtherBPs'),
              type: 'error'
            });
          })
          .finally(() => {
            this.closeDialogToSelectBPs();
          });
      }
    }
  }
};
</script>

<style scoped>
.card-container {
  display: grid;
  grid-template-rows: auto 38px;
  height: 100%;
}

.kpis-thresholds-container {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 2px;
  height: 100%;
  padding: 0 8px;
}

.health-configuration-container {
  display: grid;
  grid-template-columns: 50% 50%;
  height: 100%;
  width: 100%;
  padding: 0 8px;
}

.card {
  overflow: hidden;
}

.card-title {
  padding: 2px 4px;
  font-size: 16px;
}

.card-content {
  height: 100%;
  padding: 4px 2px;
  font-size: 10px;
}

.kpi-threshold-container {
  display: inline-grid;
  grid-template-columns: 28px 28px auto;
  grid-gap: 4px;
  width: 100%;
}

.kpi-input {
  width: 100%;
  text-align: center;
  color: #000000;
}

.kpi-input:focus {
  outline: unset;
}

[dark].kpi-input {
  color: #ffffff;
}

.kpi-input-warning {
  border-bottom: 1px solid var(--v-warning-base);
}

.kpi-input-danger {
  border-bottom: 1px solid indianred;
}

.kpi-description {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.health-configuration-options {
  display: inline-flex;
  font-size: 12px;
}

.health-configuration-label {
  line-height: 28px;
  margin-right: 8px;
}

.health-configuration-input {
  max-width: 120px;
  padding: 0;
  margin: 0;
  font-size: 12px;
}

.health-thresholds-options {
  place-self: center end;
}

.ecs-new-health-config-dialog-text {
  padding: 0px 16px !important;
}
</style>
